import {
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import clone from 'lodash-es/clone';
import React, { useState } from 'react';
import { FormProvider, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraSelect } from '../../../components/AtiraSelect';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Section } from '../../../components/Section';
import { Text } from '../../../components/Text';
import { DNDContext } from '../../../components/dnd/DNDContext';
import { DNDSortableDroppableContext } from '../../../components/dnd/DNDSortableDroppableContext';
import { atiraLinkTestIDs } from '../../../constants/test/atiralink.testIds';
import { CreateAtiraLinkDto } from '../../../model/atira-link/dto/CreateAtiraLinkDto';
import { EditAtiraLinkDto } from '../../../model/atira-link/dto/EditAtiraLinkDto';
import { AtiraLinkSocialHandlers } from '../../../model/atira-link/types/AtiraLinkSocialMediaHandlers.enum';
import { Lengths } from '../../../model/shared/enum/Lengths.enum';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Shadow } from '../../../theme/Shadow';
import { Spacing } from '../../../theme/Spacing';
import { Tooltip } from '../../../utils/local-antd';
import { moveErrorMessage } from '../utils/moveSocialErrorMessage';
import { LinkInput } from './LinkInput';
import { SocialLink } from './SocialLink';

const placeholders: Record<AtiraLinkSocialHandlers, string> = {
  WHATSAPP: 'https://wa.me/905...',
  INSTAGRAM: 'https://www.instagram.com/...',
  TWITTER: 'https://www.twitter.com/...',
  FACEBOOK: 'https://www.facebook.com/...',
  YOUTUBE: 'https://www.youtube.com/...',
  TELEGRAM: 'https://www.telegram.com/...',
  TIKTOK: 'https://www.tiktok.com/...',
  SNAPCHAT: 'https://www.snapchat.com/...',
  REDDIT: 'https://www.reddit.com/...',
  LINKEDIN: 'https://www.linkedin.com/...',
  PINTEREST: 'https://www.pinterest.com/...',
  SPOTIFY: 'https://www.spotify.com/...',
  TWITCH: 'https://www.twitch.tv/...',
  GITHUB: 'https://www.github.com/...',
};

const getPlaceholder = (value: AtiraLinkSocialHandlers): string => {
  return placeholders[value] || '';
};

const SocialsLinksWrapper = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.s};
  margin-top: ${Spacing.s};
`;

const SocialCreationWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.s};
  flex: 1;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    gap: ${Spacing.m};
  }
`;

const StyledSelect = styled(AtiraSelect)`
  .ant-select-selector {
    background-color: ${({ theme }) => theme.bgSpecialInput} !important;
    border: 0 !important;
    color: ${({ theme }) => theme.fgSpecialInput};
  }

  .ant-select-selector:hover {
    border: 0 !important;
  }
`;

const SocialLinkButtonWrapper = styled(Flex)`
  gap: ${Spacing.s};
  flex-direction: column;

  button {
    max-width: 5rem;
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    flex-direction: row;
  }
`;

const AddSocialLinkButton = styled(Button)`
  height: 2.3rem;
  width: fit-content;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    height: 2.7rem;
  }
`;

const DragOverlaySocialLink = styled(SocialLink)`
  box-shadow: ${Shadow.MD};
`;

export const AtiraLinkContentSocials: React.FC = () => {
  const [selectedOption, setSelectedOption] =
    useState<AtiraLinkSocialHandlers | null>(null);
  const [socialLink, setSocialLink] = useState('');
  const [socialLabel, setSocialLabel] = useState('');
  const [activeItemId, setActiveItemId] = useState<
    UniqueIdentifier | undefined
  >();

  const { t } = useTranslation();

  const formMethods = useFormContext<CreateAtiraLinkDto | EditAtiraLinkDto>();

  const {
    control,
    watch,
    formState: { errors },
    clearErrors,
    setError,
  } = formMethods;

  const { append, fields, replace, update, remove } = useFieldArray({
    control,
    name: 'socials',
  });

  const socials = watch('socials');

  const onAddSocial = () => {
    if (selectedOption && socialLink.trim() && socialLabel.trim()) {
      append({
        handle: selectedOption,
        label: socialLabel,
        url: socialLink,
        order: fields.length,
      });
      setSelectedOption(null);
      setSocialLink('');
      setSocialLabel('');
    }
  };

  const getSocialMediaOptions = () =>
    (
      [
        {
          value: 'WHATSAPP',
          label: t('common.whatsapp'),
        },
        { value: 'INSTAGRAM', label: t('common.instagram') },
        { value: 'TWITTER', label: t('common.twitter') },
        { value: 'FACEBOOK', label: t('common.facebook') },
        { value: 'YOUTUBE', label: t('common.youtube') },
        { value: 'TELEGRAM', label: t('common.telegram') },
        { value: 'TIKTOK', label: t('common.tiktok') },
        { value: 'SNAPCHAT', label: t('common.snapchat') },
        { value: 'REDDIT', label: t('common.reddit') },
        { value: 'LINKEDIN', label: t('common.linkedin') },
        { value: 'PINTEREST', label: t('common.pinterest') },
        { value: 'SPOTIFY', label: t('common.spotify') },
        { value: 'TWITCH', label: t('common.twitch') },
        { value: 'GITHUB', label: t('common.github') },
      ].map((item) => ({
        ...item,
        'data-testid': atiraLinkTestIDs.content.socialMedia.dropdown.option(
          AtiraLinkSocialHandlers[
            item.value as keyof typeof AtiraLinkSocialHandlers
          ] as string,
        ),
      })) as { value: AtiraLinkSocialHandlers; label: string }[]
    ).filter((item) => !socials?.map((s) => s.handle)?.includes(item.value));

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItemId(active.id);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeIndex = fields.findIndex((item) => item.id === active.id);
    const overIndex = fields.findIndex((item) => item.id === over.id);

    moveErrorMessage(activeIndex, overIndex, setError, clearErrors, errors);

    const newSocials = arrayMove(clone(socials || []), activeIndex, overIndex);

    replace(newSocials);
    setActiveItemId(undefined);
  };

  const renderDragOverlay = () => {
    if (!activeItemId) {
      return null;
    }

    const social = fields.find((item) => item.id === activeItemId);

    if (!social) {
      return null;
    }

    return (
      <DragOverlaySocialLink social={social} index={fields.indexOf(social)} />
    );
  };

  return (
    <FormProvider {...formMethods}>
      <Section title={t('common.social_media')}>
        <SocialCreationWrapper>
          <Flex
            justifyContent="space-between"
            alignItems="flex-end"
            gap="m"
            flexWrap="wrap"
          >
            <StyledSelect
              title={t('atira_link.create.content.select_social')}
              value={{
                label: selectedOption
                  ? t(`common.${selectedOption?.toLowerCase()}`)
                  : t('common.dropdown.select'),

                value: selectedOption,
              }}
              options={getSocialMediaOptions()}
              onChange={(v) => setSelectedOption(v as AtiraLinkSocialHandlers)}
              listHeight={200}
              popupMatchSelectWidth={false}
              containerStyle={{ width: '12rem' }}
              dataTestId={atiraLinkTestIDs.content.socialMedia.dropdown.open}
            />

            <LinkInput
              title={`${t('atira_link.create.content.social_label.title')}:`}
              id="social-label"
              value={socialLabel}
              placeholder={t(
                'atira_link.create.content.social_label.plcaeholder',
              )}
              onChange={(e) => setSocialLabel(e.currentTarget.value)}
              maxLength={Lengths.NAME}
              dataTestId={atiraLinkTestIDs.content.socialMedia.label}
            />
          </Flex>

          <SocialLinkButtonWrapper>
            <LinkInput
              title={`${t('common.enter_your_url')}:`}
              id="social-url"
              value={socialLink}
              placeholder={selectedOption ? getPlaceholder(selectedOption) : ''}
              onChange={(e) => setSocialLink(e.currentTarget.value)}
              maxLength={Lengths.DESCRIPTION}
              dataTestId={atiraLinkTestIDs.content.socialMedia.value}
              type="url"
            />

            <Tooltip title={t('atira_link.create.socials.add.tooltip')}>
              <AddSocialLinkButton
                icon={faPlus}
                onClick={onAddSocial}
                disabled={!selectedOption || !socialLink || !socialLabel}
                dataTestId={atiraLinkTestIDs.content.socialMedia.add}
              />
            </Tooltip>
          </SocialLinkButtonWrapper>
        </SocialCreationWrapper>

        {socials?.length ? (
          <SocialsLinksWrapper>
            <Text fontWeight={'bold'}>
              {t('atira_link.create.socials.second_title')}
            </Text>
            <DNDContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
              <DNDSortableDroppableContext id="dropZone" items={fields}>
                <Flex flexDirection="column" gap="s">
                  {fields.map((item, index) => (
                    <SocialLink
                      social={item}
                      key={item.id}
                      update={update}
                      remove={remove}
                      replace={replace}
                      fields={fields}
                      index={index}
                    />
                  ))}
                </Flex>
              </DNDSortableDroppableContext>

              <DragOverlay
                dropAnimation={{
                  duration: 500,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }}
              >
                {renderDragOverlay()}
              </DragOverlay>
            </DNDContext>
          </SocialsLinksWrapper>
        ) : null}
      </Section>
    </FormProvider>
  );
};
